import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import { IBusinessRule } from "../../model/evaluation/BusinessRule";
import GetBusinessRulesInteractor from "../../interactor/evaluation/GetBusinessRulesInteractor";
import CreateBusinessRuleInteractor from "../../interactor/evaluation/CreateBusinessRuleInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import UpdateBusinessRuleInteractor from "../../interactor/evaluation/UpdateBusinessRuleInteractor";

export interface TBusinessRulesPresenter extends TLoadingAwarePresenter {
  businessRules: IBusinessRule[] | undefined;
}
export interface IBusinessRulesPresenter
  extends TBusinessRulesPresenter,
    TPresentable {
  getBusinessRules(): Promise<void>;
  setBusinessRules(businessRules: IBusinessRule[]): void;
  createBusinessRule(businessRule: IBusinessRule): void;
  updateBusinessRule(businessRule: IBusinessRule): void;
}

const defaultState: TBusinessRulesPresenter = {
  businessRules: []
};

const BusinessRulesPresenter = withStore<
  IBusinessRulesPresenter,
  TBusinessRulesPresenter
>(({ application, store, loader, translate }): IBusinessRulesPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TBusinessRulesPresenter>();

  loader.start("businessRulesLoader");

  const setBusinessRules = (businessRules: IBusinessRule[]) => {
    _store.update({ businessRules: businessRules });
    loader.stop("businessRulesLoader");
  };

  const getBusinessRules = async () => {
    try {
      let businessRules = await _application.container
        .resolve<GetBusinessRulesInteractor>("getBusinessRules")
        .execute();
      _store.update({ businessRules });
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createBusinessRule = async (businessRule: IBusinessRule) => {
    try {
      await _application.container
        .resolve<CreateBusinessRuleInteractor>("createBusinessRule")
        .execute(businessRule);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("businessRules_page.messages.create_businessRule_success")
        );
      await getBusinessRules();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const updateBusinessRule = async (businessRule: IBusinessRule) => {
    try {
      await _application.container
        .resolve<UpdateBusinessRuleInteractor>("updateBusinessRule")
        .execute(businessRule);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("businessRules_page.messages.update_businessRule_success")
        );
      await getBusinessRules();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  return {
    ...state,

    store: _store,
    loader,
    application: _application,
    translate,
    getBusinessRules,
    setBusinessRules,
    createBusinessRule,
    updateBusinessRule
  };
}, defaultState);

export default BusinessRulesPresenter;
